import { Formik } from 'formik'
import { useEffect, useState } from 'react'
import Input from '../../../../../../components/form-control/Input'
import Button from '../../../../../../components/form-control/Button'
import { AlertTriangle, Calculator, Car } from 'lucide-react'
import * as Yup from 'yup'
import { bodyTypeMappingFromNumberToString, calculateNumberOfDaysBetweenTwoPeriods, isComprehensive100Cover, isComprehensive85Cover, isComprehensive90Cover, isPlainComprehensiveCover, predictEndDateFromStartDate, removeUnderscoresAndCapitalize, showExcessBoughtField, showNCDFieldOnPolicyJourney, showToast, usageMapToString } from '../../../../../../utils/miscFunctions'
import SelectDropdown from '../../../../../../components/form-control/SelectDropdown'
import usePartner from '../../../../../../hooks/partnerHook'
import { getEnumsFromState } from '../../../../../../states/slices/resourceSlice'
import { useDispatch, useSelector } from 'react-redux'
import { IEnum } from '../../../../../../resources/interfaces'
import { checkFleetPolicyQuote, checkPolicyQuote, getActiveRiskTypes } from '../../../../../../api/requests'
import { useQuery } from '@tanstack/react-query'
import { ISelectMenuItemValue } from '../../../../../../components/form-control/SelectMenu'
import DatePickerInput from '../../../../../../components/form-control/DatePickerInput'
import moment from 'moment'
import { FormData } from './Calculator'
import useUserAccount from '../../../../../../hooks/userAccountHook'
import { CheckBoxInput } from '../../../../../../components/form-control/CheckBoxInput'
import IntermediarySelectorInput from '../../../../../partners/insurers/components/IntermediarySelectorInput'
import { getQuoteWidgetPayloadFromState, SET_FLEET_VEHICLES, SET_IS_FLEET_QUOTE_CALCULATOR, SET_QUOTE_WIDGET_FLEET_PAYLOAD, SET_QUOTE_WIDGET_PAYLOAD, SET_QUOTE_WIDGET_RESPONSE } from '../../../../../../states/slices/quoteWidgetSlice'
import { useNavigate, useSearchParams } from 'react-router-dom'
import CreatePolicyFleet from '../../../../../shared/policies/auto/create-policy/components/CreatePolicyFleet'
import { FleetVehicle, PolicyWizardData, VehicleDetails } from '../../../../../shared/policies/auto/create-policy'
import { FleetPolicyDetailsDTO, fleetVehicle } from '../../../../../shared/policies/auto/create-policy/components/PolicyDetailsStep'
import useLayoutHook from '../../../../../../hooks/layoutHook'
import { getPendingActions } from '../../../../../../states/slices/globalSlice'
import Banner from '../../../../../../components/ui/Banner'
import useRoute from '../../../../../../hooks/routesHook'

interface QuoteWidgetFormProps {
    formData?: FormData;
    onUpdate: (data: FormData & { quoteResponse?: any }) => void;
    onNext: () => void;
    onclose: () => void;
}

const QuoteWidgetCalculatorForm = ({ onNext, onUpdate, onclose, formData }: QuoteWidgetFormProps) => {
    const { fetchCompanyPortfolio } = usePartner()
    const { user } = useUserAccount()
    const dispatch = useDispatch()
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { getDashboardBase } = useRoute();
    const isRenewal = searchParams.get('isRenewal');
    const suffix = isRenewal === 'true' ? '_renewal' : '';
    const [selectedCompany, setSelectedCompany] = useState<number | undefined>()
    const [riskTypes, setRiskTypes] = useState<any>()
    const enums = useSelector(getEnumsFromState)
    const currencyCodes: IEnum[] = enums?.currencyCodes
    const [isSubmitting, setIsSubmitting] = useState(false)
    const isAsanti = user?.company?.name?.toLowerCase().includes('asanti');
    const [currentStep, setCurrentStep] = useState(1);
    const [vehicleType, setVehicleType] = useState<'single' | 'fleet'>('single');
    const [vehicleFromCSV, setVehicleFromCSV] = useState<FleetVehicle[]>([]);
    const { screenWidth } = useLayoutHook();

    const [wizardData, setWizardData] = useState<Partial<PolicyWizardData>>(() => {
        // Load saved data from localStorage if it exists
        const savedData = localStorage.getItem(`policyWizardData${suffix}`);
        return savedData ? JSON.parse(savedData) : {};
    });
    const fetchAllActiveRiskTypes = () => {
        getActiveRiskTypes()
            .then((response: any) => {
                setRiskTypes(response);
            })
            .catch((error: any) => {
                console.error('Error fetching risk types:', error);
            });
    };

    useQuery({
        queryKey: ['getAllActiveRiskTypes'],
        queryFn: () => fetchAllActiveRiskTypes(),
    })

    const { data: insurerPortfolio = [] } = useQuery({
        queryKey: ['insurerPortfolio', selectedCompany],
        queryFn: () => {
            if (!selectedCompany) return []
            return fetchCompanyPortfolio(selectedCompany)
        },
        enabled: !!selectedCompany,
    })


    const updateVehicleData = (data: VehicleDetails | FleetVehicle[], vehicleType: 'single' | 'fleet') => {
        if (vehicleType === 'single') {
            setWizardData(prev => ({
                ...prev,
                vehicle: data as VehicleDetails,
                vehicles: undefined,
                vehicleType: 'single'
            }));
        } else {
            // For fleet, ensure we're working with an array of FleetVehicle
            const fleetVehicles: FleetVehicle[] = Array.isArray(data)
                ? data as FleetVehicle[]
                : [data as FleetVehicle];

            // Update the wizard data with proper typing
            setWizardData(prev => ({
                ...prev,
                vehicle: undefined,
                vehicles: fleetVehicles,
                vehicleType: 'fleet' as const
            }));
        }
    };
    const getCoverTypeNameById = (insurerPortfolioId: any) => {
        const id = Number(insurerPortfolioId);
        if (!id || !Array.isArray(insurerPortfolio)) return '';
        const match = insurerPortfolio.find((p: any) => Number(p?.id) === id);
        return String(match?.name ?? '').toLowerCase();
    }

    const isComprehensiveCover = (insurerPortfolioId: any) => {
        const name = getCoverTypeNameById(insurerPortfolioId);
        return name.includes('comprehensive');
    }

    const isThirdPartyFireTheftCover = (insurerPortfolioId: any) => {
        const name = getCoverTypeNameById(insurerPortfolioId);
        const normalized = name.replace(/\s+/g, ' ').trim();
        return (
            normalized.includes('third party fire') ||
            normalized.includes('third-party fire') ||
            normalized.includes('third party fire & theft') ||
            normalized.includes('third party fire and theft')
        );
    }

    const isThirdPartyCover = (insurerPortfolioId: any) => {
        const name = getCoverTypeNameById(insurerPortfolioId);
        const normalized = name.replace(/\s+/g, ' ').trim();
        return (
            normalized.includes('third party') ||
            normalized.includes('third-party') ||
            normalized.includes('third party') ||
            normalized.includes('third party')
        );
    }

    const handleSubmit = async (values: any) => {
        const payload: any = {
            insurerPortfolioId: values.insurerPortfolioId || null,
            excessBought: values.excessBought === 'yes' ? true : false,
            riskTypeId: values.riskTypeId || null,
            extraTppdlLimit: values.extraTppdlLimit || 0,
            umbrellaLimit: values.umbrellaLimit || 0,
            startDate: moment(values.startDate).format('YYYY-MM-DD') || null,
            endDate: moment(values.endDate).format('YYYY-MM-DD') || null,
            currency: values.currency || 'GHS',
            currencyRate: values.currencyRate || 1,
            registrationYear: values.registrationYear || null,
            sumInsured: values.insuredValue || 0,
            noOfSeats: values.maxSeat || 0,
            leadSource: values.leadSource || null,
            insurerId: values.insuranceCompany,
            applyNcd: values.applyNcd || false,
            ncdRate: values.ncdRate || null,
        }

        const fleetVehicles: fleetVehicle[] = (vehicleFromCSV || []).map((vehicle: FleetVehicle) => {
            return {
                cc: vehicle.cc,
                chassisNumber: vehicle.chassisNumber,
                excessBought: vehicle.excessBought === 'yes',
                extraTppdlLimit: vehicle.extraTppdlLimit,
                issuedValue: vehicle.issuedValue,
                make: vehicle.maker,
                manufacturingYear: vehicle.yearIssued,
                maxSeat: vehicle.maxSeat,
                model: vehicle.model,
                partsAvailability: undefined,
                registrationNumber: vehicle.registrationNumber,
                registrationYear: vehicle.registrationYear,
                riskTypeId: vehicle.riskTypeId,
                vehicleBodyType: bodyTypeMappingFromNumberToString[vehicle.vehicleBodyType],
                vehicleColour: vehicle.color,
                vehicleUsages: usageMapToString[Number(vehicle.vehicleUsage)],
                insurerPortfolioId: vehicle.insurerPortfolioId
            }
        })
        const fleetPayload: FleetPolicyDetailsDTO = {
            umbrellaLimit: values.umbrellaLimit,
            startDate: moment(values.startDate).format('YYYY-MM-DD'),
            endDate: moment(values.endDate).format('YYYY-MM-DD'),
            customerId: null,
            fleetMotorPolicyRequest: fleetVehicles,
            insurerId: Number(selectedCompany),
            currencyCode: values.currency,
            currencyRate: values.currencyRate,
            customFleetDiscountPercentage: values.customFleetDiscountPercentage,
            leadSource: values.leadSource,
        }

        dispatch(SET_QUOTE_WIDGET_PAYLOAD(payload))
        // Keep the fleet request too so the editable-premium flow can re-quote
        // the fleet with an overriddenPremium.
        if (vehicleType === 'fleet') dispatch(SET_QUOTE_WIDGET_FLEET_PAYLOAD(fleetPayload))

        setIsSubmitting(true)

        try {
            const response: any = vehicleType === 'single' ? await checkPolicyQuote(payload) : await checkFleetPolicyQuote(fleetPayload);
            if (response) {
                dispatch(SET_QUOTE_WIDGET_RESPONSE(response?.data ?? null))
                showToast.success(response?.message || 'Quote calculated successfully')
                onUpdate({
                    ...values,
                    quoteResponse: response?.data
                })
                vehicleFromCSV && updateVehicleData(vehicleFromCSV, 'single');
                onNext()
            }
        } catch (error: any) {
            showToast.error(error?.description ?? 'Could not calculate the quote. Please try again.')
            setIsSubmitting(false)
        } finally {
            setIsSubmitting(false)
        }
    }

    useEffect(() => {
        if (formData?.insurerPortfolioId && !selectedCompany) {
            setSelectedCompany(formData.insurerPortfolioId);
        }
    }, [formData?.insurerPortfolioId, selectedCompany]);

    useEffect(() => {

        dispatch(SET_IS_FLEET_QUOTE_CALCULATOR(vehicleType === 'fleet' ? true : false));
    }, [vehicleType])

    const pendingActions = useSelector(getPendingActions);
    const insurerConfiguration = pendingActions?.filter((message: string) => message?.includes('insurance company'))[0]
    return (
        <div className="bg-white dark:bg-darkBg rounded-lg shadow-sm p-6 ">
            <div className='py-4'>
                {insurerConfiguration && <Banner
                    // key={index}
                    variant={'info'}
                    message={insurerConfiguration}
                    position="top"
                    isSticky={true}
                    dismissible={true}
                    onDismiss={() => { }}
                    action={{
                        label: 'Add Insurer',
                        onClick: () => {
                            onclose()
                            navigate(`${getDashboardBase()}/settings?tab=insuranceCompanies`);

                        }
                    }}
                />}
            </div>

            <div className="flex flex-col justify-between dark:border-darkBg-border rounded-lg p-6">
                <div className="max-w-md mx-auto">
                    {/* Toggle Switch */}
                    <div className="bg-white dark:bg-darkBg dark:border-darkBg-border rounded-lg p-1 shadow-sm border border-gray-200">
                        <div className="grid grid-cols-2 gap-1">
                            {/* Single Vehicle Option */}
                            <button
                                onClick={() => setVehicleType('single')}
                                className={`px-4 py-3 rounded-md text-sm font-medium transition-all ${vehicleType === 'single'
                                    ? 'bg-primary-main text-white'
                                    : 'text-gray-600 dark:text-darkBg-subText hover:bg-gray-100 dark:hover:bg-darkBg-border'
                                    }`}
                            >
                                <div className="flex items-center justify-center">
                                    <Car className="h-4 w-4 mr-2" />
                                    {screenWidth <= 550 ? 'Single' : 'Single Vehicle'}
                                </div>
                            </button>

                            {/* Fleet Option */}
                            <button
                                onClick={() => setVehicleType('fleet')}
                                className={`px-4 py-3 rounded-md text-sm font-medium transition-all ${vehicleType === 'fleet'
                                    ? 'bg-primary-main text-white'
                                    : 'text-gray-600 dark:text-darkBg-subText hover:bg-gray-100 dark:hover:bg-darkBg-border'
                                    }`}
                            >
                                <div className="flex items-center justify-center">
                                    <div className="flex items-center mr-2">
                                        <Car className="h-3 w-3" />
                                        <Car className="h-3 w-3 -ml-1" />
                                    </div>
                                    Fleet
                                </div>
                            </button>
                        </div>
                    </div>

                    {/* Current Selection Description */}
                    <div className="mt-4 text-center">
                        <p className="text-sm text-gray-600 dark:text-darkBg-subText">
                            {vehicleType === 'single'
                                ? "Individual coverage for your customer's private vehicle with customized protection options"
                                : 'Coverage for multiple vehicles with exclusive fleet benefits'
                            }
                        </p>
                    </div>

                    {/* Info Message */}
                    <div className="mt-4 flex justify-center">
                        <AlertTriangle className="h-4 w-4 md:h-3 md:w-3 mr-1 mt-0 md:mt-1 dark:text-white text-black" />
                        <p className="text-center text-xs text-gray-500 dark:text-darkBg-mainText flex items-center justify-center">

                            Fleet insurance is recommended for businesses with 3 or more vehicles
                        </p>
                    </div>
                </div>
            </div>

            <Formik
                initialValues={{
                    registrationYear: formData?.registrationYear || undefined,
                    insuredValue: formData?.insuredValue || undefined,
                    maxSeat: formData?.maxSeat || undefined,
                    insurerPortfolioId: formData?.insurerPortfolioId || undefined,
                    excessBought: formData?.excessBought || undefined,
                    riskTypeId: formData?.riskTypeId || undefined,
                    extraTppdlLimit: formData?.extraTppdlLimit || undefined,
                    umbrellaLimit: formData?.umbrellaLimit || undefined,
                    startDate: formData?.startDate || undefined,
                    endDate: formData?.endDate || undefined,
                    insuranceCompany: formData?.insuranceCompany || undefined,
                    currency: formData?.currency || 'GHS',
                    currencyRate: formData?.currencyRate || undefined,
                    leadSource: formData?.leadSource || undefined,
                    applyNcd: formData?.applyNcd,
                    ncdRate: formData?.ncdRate,
                    customFleetDiscountPercentage: formData?.customFleetDiscountPercentage || undefined,
                }}
                validateOnChange={true}
                validateOnBlur={true}
                validationSchema={Yup.object({
                    registrationYear: vehicleType === 'single' ? Yup.number().required("Registration year is required") : undefined as any,
                    insuredValue: vehicleType === 'single' ? Yup.number().when(['insurerPortfolioId'], {
                        is: (insurerPortfolioId: number) =>
                            isComprehensiveCover(insurerPortfolioId) || isThirdPartyFireTheftCover(insurerPortfolioId),
                        then: (schema) => schema.required("Issued value is required"),
                        otherwise: (schema) => schema.notRequired().nullable(),
                    }) : undefined as any,
                    maxSeat: vehicleType === 'single' ? Yup.number().required("Max seat is required") : undefined as any,
                    excessBought: Yup.string()
                        .when(['insurerPortfolioId'], {
                            is: (insurerPortfolioId: number) =>
                                showExcessBoughtField(Number(insurerPortfolioId)) && isPlainComprehensiveCover(insurerPortfolioId, insurerPortfolio),
                            then: (schema) => schema.required('Excess bought is required'),
                            otherwise: (schema) => schema.notRequired()
                        }),
                    insuranceCompany: Yup.string().trim().required("Insurance company is required"),
                    umbrellaLimit: Yup.number().nullable().min(0, 'Cannot be negative').notRequired(),
                    startDate: Yup.string().trim().required("Start date is required").nullable()
                        .test('is-valid-range', 'Start date must be before end date', function (startDate) {
                            const { endDate } = this.parent;
                            if (!startDate || !endDate) return true;
                            return new Date(startDate) < new Date(endDate);
                        }),
                    endDate: Yup.string().trim().required("End date is required").nullable()
                        .test('is-within-year', 'Policy duration cannot exceed 1 year', function (endDate) {
                            const { startDate } = this.parent;
                            if (!startDate || !endDate) return true;
                            const start = new Date(startDate);
                            const end = new Date(endDate);
                            const oneYearFromStart = new Date(start);
                            oneYearFromStart.setFullYear(start.getFullYear() + 1);
                            return end <= oneYearFromStart;
                        }),
                    insurerPortfolioId: vehicleType === 'single' ? Yup.number().required("Insurer portfolio is required") : undefined as any,
                    riskTypeId: vehicleType === 'single' ? Yup.number().required("Risk type is required") : undefined as any,
                    extraTppdlLimit: Yup.number().nullable().min(0, 'Cannot be negative').notRequired(),
                    currency: Yup.string().nullable().required('Currency is required'),
                    ncdRate: Yup.number()
                        .when('applyNcd', {
                            is: true,
                            then: Yup.number()
                                // .required('NCD rate is required when NCD is applied')
                                .min(1, 'NCD rate cannot be less than 1')
                                .max(100, 'NCD rate cannot be more than 100'),
                            otherwise: Yup.number().nullable()
                        }),

                })}
                onSubmit={(values) => {
                    handleSubmit(values)
                }}>
                {({ values, errors, handleSubmit, handleChange, setFieldValue, handleBlur, touched }) => {
                    console.log("errors", errors)
                    return (
                        <form className="space-y-10" onSubmit={handleSubmit}>



                            {vehicleType === 'single' ? (<div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-6 rounded-lg">
                                <IntermediarySelectorInput
                                    name="insuranceCompany"
                                    listSelectedValue={values.insuranceCompany}
                                    onChange={(val: number) => {
                                        setFieldValue('insuranceCompany', val);
                                        setSelectedCompany(val);
                                    }}
                                    onBlur={handleBlur}
                                    error={touched.insuranceCompany ? errors.insuranceCompany : undefined}
                                    helperText={touched?.insuranceCompany ? errors.insuranceCompany : undefined}
                                />

                                <SelectDropdown
                                    label="Cover Type"
                                    name="insurerPortfolioId"
                                    listSelectedValue={values.insurerPortfolioId}
                                    onChange={(val: number) => {
                                        setFieldValue('insurerPortfolioId', val);
                                        if (isComprehensive100Cover(val, insurerPortfolio)) {
                                            setFieldValue('excessBought', 'no');
                                        } else if (isComprehensive90Cover(val, insurerPortfolio) || isComprehensive85Cover(val, insurerPortfolio)) {
                                            setFieldValue('excessBought', 'yes');
                                        }
                                    }}
                                    onBlur={handleBlur}
                                    search
                                    searchPlaceHolder="Search by cover type ..."
                                    list={Array.isArray(insurerPortfolio)
                                        ? insurerPortfolio.map((item: any) => ({
                                            name: item?.name || '',
                                            value: item?.id || '',
                                        }))
                                        : []}
                                    placeholder="Select cover type"
                                    error={touched.insurerPortfolioId ? errors.insurerPortfolioId : undefined}
                                    helperText={touched?.insurerPortfolioId ? errors.insurerPortfolioId : undefined}
                                    required
                                />

                                <SelectDropdown
                                    label="Risk Type"
                                    name="riskTypeId"
                                    listSelectedValue={values.riskTypeId}
                                    onChange={(val: ISelectMenuItemValue | null) => {
                                        setFieldValue('riskTypeId', val);
                                    }}
                                    onBlur={handleBlur}
                                    search
                                    searchPlaceHolder="Search by risk type ..."
                                    list={riskTypes?.map((riskType: any) => ({
                                        name: riskType?.name,
                                        value: riskType?.id,
                                    }))}
                                    placeholder="Select risk type"
                                    error={touched.riskTypeId ? errors.riskTypeId : undefined}
                                    helperText={touched?.riskTypeId ? errors.riskTypeId : undefined}
                                    required
                                />

                                {isComprehensive100Cover(values.insurerPortfolioId, insurerPortfolio) ? null
                                    : isComprehensive90Cover(values.insurerPortfolioId, insurerPortfolio) ? (
                                        <Input
                                            type="text"
                                            label="Excess Bought"
                                            name="excessBought"
                                            value="10%"
                                            disabled
                                        />
                                    )
                                    : isComprehensive85Cover(values.insurerPortfolioId, insurerPortfolio) ? (
                                        <Input
                                            type="text"
                                            label="Excess Bought"
                                            name="excessBought"
                                            value="15%"
                                            disabled
                                        />
                                    )
                                    : showExcessBoughtField(Number(values.insurerPortfolioId)) && isPlainComprehensiveCover(values.insurerPortfolioId, insurerPortfolio) ? (
                                        <SelectDropdown
                                            label="Excess Bought"
                                            name="excessBought"
                                            listSelectedValue={values.excessBought}
                                            onChange={(val: ISelectMenuItemValue | null) => {
                                                setFieldValue('excessBought', val);
                                            }}
                                            onBlur={handleBlur}
                                            list={[{ name: 'Yes', value: 'yes' }, { name: 'No', value: 'no' }]}
                                            placeholder="Select excess bought"
                                            error={touched.excessBought ? errors.excessBought : undefined}
                                            helperText={touched?.excessBought ? errors.excessBought : undefined}
                                            required
                                        />
                                    )
                                    : null}

                                {(isComprehensiveCover(values.insurerPortfolioId) ||
                                    isThirdPartyFireTheftCover(values.insurerPortfolioId)) && (
                                        <Input
                                            type="number"
                                            id="insuredValue"
                                            label={`Vehicle's Insured Value`}
                                            value={values?.insuredValue}
                                            onChange={handleChange}
                                            placeholder="Enter vehicle insured value"
                                            onBlur={handleBlur}
                                            required={vehicleType === 'single'}
                                            error={touched.insuredValue ? errors.insuredValue : undefined}
                                            helperText={touched?.insuredValue ? errors.insuredValue : undefined}
                                        />
                                    )}

                                <Input
                                    type="number"
                                    id="registrationYear"
                                    label="Registration Year"
                                    value={values?.registrationYear}
                                    min={values.registrationYear ?? 0}
                                    onChange={handleChange}
                                    placeholder="Enter registration year"
                                    onBlur={handleBlur}
                                    required={vehicleType === 'single'}
                                    error={touched.registrationYear ? errors.registrationYear : undefined}
                                    helperText={touched?.registrationYear ? errors.registrationYear : undefined}
                                />

                                <Input
                                    type="number"
                                    id="maxSeat"
                                    label="Number of Passengers"
                                    value={values?.maxSeat}
                                    min={1}
                                    onChange={handleChange}
                                    placeholder="Enter number of passengers"
                                    onBlur={handleBlur}
                                    required={vehicleType === 'single'}
                                    error={touched.maxSeat ? errors.maxSeat : undefined}
                                    helperText={touched?.maxSeat ? errors.maxSeat : undefined}
                                />

                                <DatePickerInput
                                    id="startDate"
                                    label='Start Date'
                                    selected={values?.startDate ? new Date(values?.startDate) : null}
                                    placeholder="Select start date"
                                    onChange={(value) => {
                                        setFieldValue("startDate", value);
                                        if (value) {
                                            setFieldValue("endDate", predictEndDateFromStartDate(value))
                                        }
                                    }}
                                    required
                                    allowEarlierSelections={false}
                                    error={touched.startDate ? errors.startDate : undefined}
                                    helperText={touched?.startDate ? errors.startDate : undefined}
                                />

                                <DatePickerInput
                                    id="endDate"
                                    label='End Date'
                                    selected={values?.endDate ? new Date(values?.endDate) : null}
                                    placeholder="Select end date"
                                    onChange={(value) => {
                                        setFieldValue("endDate", value);
                                    }}
                                    required
                                    error={touched.endDate ? errors.endDate : undefined}
                                    helperText={touched?.endDate ? errors.endDate : undefined}
                                />

                                <Input
                                    type="text"
                                    id=""
                                    label='Total Days'
                                    value={calculateNumberOfDaysBetweenTwoPeriods(values?.startDate, values?.endDate)}
                                    placeholder=""
                                    className='dark:text-darkBg-mainText'
                                    disabled
                                />

                                <SelectDropdown
                                    label="Currency"
                                    name="currency"
                                    listSelectedValue={values.currency}
                                    onChange={(val: ISelectMenuItemValue | null) => {
                                        setFieldValue('currency', val);
                                    }}
                                    onBlur={handleBlur}
                                    search
                                    searchPlaceHolder="Search by currency ..."
                                    list={currencyCodes?.map((currency: IEnum) => ({
                                        name: currency.title,
                                        value: currency.key,
                                    }))}
                                    placeholder="Select currency"
                                    error={touched.currency ? errors.currency : undefined}
                                    helperText={touched?.currency ? errors.currency : undefined}
                                    required
                                />

                                {isAsanti &&
                                    <SelectDropdown
                                        name="leadSource"
                                        label="Lead Source"
                                        listSelectedValue={values.leadSource}
                                        onChange={(option: string | null) => setFieldValue('leadSource', option)}
                                        list={enums?.leadSources?.map((leadSource: IEnum) => ({
                                            name: removeUnderscoresAndCapitalize(leadSource.title),
                                            value: leadSource.key,
                                        }))}
                                        placeholder="Select lead source"
                                        error={touched.leadSource ? errors.leadSource : undefined}
                                        helperText={touched?.leadSource ? errors.leadSource : undefined}
                                    />
                                }

                                {values.currency && values.currency !== "GHS" && (
                                    <Input
                                        type="number"
                                        id="currencyRate"
                                        label='Currency Rate'
                                        value={values?.currencyRate}
                                        placeholder="Enter currency rate"
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        max={1}
                                        append={"%"}
                                        error={touched.currencyRate ? errors.currencyRate : undefined}
                                        helperText={touched?.currencyRate ? errors.currencyRate : undefined}
                                    />
                                )}

                                {!isThirdPartyCover(values.insurerPortfolioId) &&
                                    <Input
                                        type="number"
                                        label="Extra Third Party Property Damage Limit"
                                        name="extraTppdlLimit"
                                        value={values.extraTppdlLimit}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        placeholder="Enter extra TPPDL"
                                        error={touched.extraTppdlLimit ? errors.extraTppdlLimit : undefined}
                                        helperText={touched?.extraTppdlLimit ? errors.extraTppdlLimit : undefined}
                                    />
                                }

                                {!isThirdPartyCover(values.insurerPortfolioId) &&
                                    <Input
                                        type="number"
                                        label="Umbrella Limit"
                                        name="umbrellaLimit"
                                        value={values.umbrellaLimit}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        placeholder="Enter umbrella limit"
                                        error={touched.umbrellaLimit ? errors.umbrellaLimit : undefined}
                                        helperText={touched?.umbrellaLimit ? errors.umbrellaLimit : undefined}
                                    />
                                }


                                {showNCDFieldOnPolicyJourney(values.insurerPortfolioId, insurerPortfolio) && (
                                    <div className="space-y-4 justify-end flex flex-col">

                                        <div className="flex items-end">
                                            <CheckBoxInput
                                                label='Do you want to apply NCD?'
                                                name={'applyNcd'}
                                                checked={values.applyNcd || false}
                                                onChange={(value: boolean) => { setFieldValue('applyNcd', value) }}
                                                labelStyles="!whitespace-nowrap"
                                                error={touched?.applyNcd ? errors.applyNcd : undefined}
                                                helperText={touched?.applyNcd ? errors.applyNcd : undefined}
                                            />
                                        </div>

                                        {values.applyNcd &&
                                            <Input
                                                type="number"
                                                id="ncdRate"
                                                name="ncdRate"
                                                placeholder="Enter NCD rate (Optional)"
                                                description="By applying NCD, renewal notice confirming customers NCD should be available for upload.(Default NCD rate for the registration year will be applied if not specified)"
                                                value={values?.ncdRate}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                append={"%"}
                                                min={1}
                                                max={100}
                                                error={touched?.ncdRate ? errors.ncdRate : undefined}
                                                helperText={touched?.ncdRate ? errors.ncdRate : undefined}
                                            />}

                                    </div>
                                )}
                            </div>) :
                                (
                                    <div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-6 rounded-lg">
                                            <IntermediarySelectorInput
                                                name="insuranceCompany"
                                                listSelectedValue={values.insuranceCompany}
                                                onChange={(val: number) => {
                                                    setFieldValue('insuranceCompany', val);
                                                    setSelectedCompany(val);
                                                }}
                                                onBlur={handleBlur}
                                                error={touched.insuranceCompany ? errors.insuranceCompany : undefined}
                                                helperText={touched?.insuranceCompany ? errors.insuranceCompany : undefined}
                                            />

                                            <DatePickerInput
                                                id="startDate"
                                                label='Start Date'
                                                selected={values?.startDate ? new Date(values?.startDate) : null}
                                                placeholder="Select start date"
                                                onChange={(value) => {
                                                    setFieldValue("startDate", value);
                                                    if (value) {
                                                        setFieldValue("endDate", predictEndDateFromStartDate(value))
                                                    }
                                                }}
                                                required
                                                allowEarlierSelections={false}
                                                error={touched.startDate ? errors.startDate : undefined}
                                                helperText={touched?.startDate ? errors.startDate : undefined}
                                            />

                                            <DatePickerInput
                                                id="endDate"
                                                label='End Date'
                                                selected={values?.endDate ? new Date(values?.endDate) : null}
                                                placeholder="Select end date"
                                                onChange={(value) => {
                                                    setFieldValue("endDate", value);
                                                }}
                                                required
                                                error={touched.endDate ? errors.endDate : undefined}
                                                helperText={touched?.endDate ? errors.endDate : undefined}
                                            />

                                            <Input
                                                type="text"
                                                id=""
                                                label='Total Days'
                                                value={calculateNumberOfDaysBetweenTwoPeriods(values?.startDate, values?.endDate)}
                                                placeholder=""
                                                className='dark:text-darkBg-mainText'
                                                disabled
                                            />
                                            
                                            <SelectDropdown
                                                label="Currency"
                                                name="currency"
                                                listSelectedValue={values.currency}
                                                onChange={(val: ISelectMenuItemValue | null) => {
                                                    setFieldValue('currency', val);
                                                }}
                                                onBlur={handleBlur}
                                                search
                                                searchPlaceHolder="Search by currency ..."
                                                list={currencyCodes?.map((currency: IEnum) => ({
                                                    name: currency.title,
                                                    value: currency.key,
                                                }))}
                                                placeholder="Select currency"
                                                error={touched.currency ? errors.currency : undefined}
                                                helperText={touched?.currency ? errors.currency : undefined}
                                                required
                                            />

                                            <Input
                                                type="number"
                                                id="customFleetDiscountPercentage"
                                                label='Fleet Discount'
                                                value={values?.customFleetDiscountPercentage}
                                                placeholder="Enter fleet discount"
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                append={"%"}
                                                error={touched.customFleetDiscountPercentage ? errors.customFleetDiscountPercentage : undefined}
                                                helperText={touched?.customFleetDiscountPercentage ? errors.customFleetDiscountPercentage : undefined}
                                            />
                                            
                                            <Input
                                                type="number"
                                                label="Umbrella Limit"
                                                name="umbrellaLimit"
                                                value={values.umbrellaLimit}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                placeholder="Enter umbrella limit"
                                                error={touched.umbrellaLimit ? errors.umbrellaLimit : undefined}
                                                helperText={touched?.umbrellaLimit ? errors.umbrellaLimit : undefined}
                                            />
                                        </div>
                                        
                                        <CreatePolicyFleet
                                            key={'fleet-0'}
                                            vehicles={vehicleFromCSV || []}
                                            fleetCSV={undefined}
                                            onVehiclesUpdate={(updatedVehicles: FleetVehicle[], csvFile?: File) => {
                                                const vehiclesWithCSV = updatedVehicles.map((v, i) =>
                                                    i === 0 && csvFile ? { ...v, fleetCSV: csvFile } : v
                                                );
                                                setVehicleFromCSV(vehiclesWithCSV);
                                                dispatch(SET_FLEET_VEHICLES(vehiclesWithCSV))
                                            }}
                                            onNext={onNext}
                                            data={[]}
                                            onPrevious={() => { }}
                                        />
                                    </div>
                                )}




                            {/* Navigation */}
                            <div className="flex justify-end mt-4">
                                <Button
                                    type="submit"
                                    label={isSubmitting ? "Checking..." : "Check Quote"}
                                    bgColor="primary"
                                    appendIcon={<Calculator className="h-4 w-4" />}
                                    disabled={isSubmitting}
                                    loading={isSubmitting}
                                />
                            </div>
                        </form>
                    )
                }}
            </Formik>


        </div>
    )
}

export default QuoteWidgetCalculatorForm